import { useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { Phone, ArrowDown, ShieldCheck, MapPin, ClipboardCheck } from 'lucide-react';
import { scrollToId } from '../lib/scroll';
import { easeOut } from './Reveal';

const LINES = [
  { text: 'Supporting people', accent: false },
  { text: 'to live', accent: false },
  { text: 'fuller lives.', accent: true },
];

function TiltCard() {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 15 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 120, damping: 15 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <div className="[perspective:1200px] w-full max-w-md" style={{ transformStyle: 'preserve-3d' }}>
      <motion.div
        ref={ref}
        data-testid="hero-badge-card"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        initial={{ opacity: 0, y: 48 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 1.1, ease: easeOut }}
        className="relative rounded-[2rem] bg-navy/50 backdrop-blur-xl p-8 lg:p-10 shadow-lift"
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center text-center gap-4"
          style={{ transform: 'translateZ(50px)' }}
        >
          <img
            src="/cfa-cover.png"
            alt="Care For All Support Services logo"
            className="h-24 w-24 rounded-full bg-white object-cover shadow-lift ring-4 ring-white/40"
          />
          <div>
            <p className="font-display font-extrabold text-2xl text-white tracking-tight">
              Care For All <span className="text-cyan-300">– Support Services</span>
            </p>
            <p className="font-display italic font-light text-lg text-white/95 mt-1">Here to serve</p>
          </div>
        </motion.div>
        <div className="mt-7 space-y-3 border-t border-white/15 pt-6" style={{ transform: 'translateZ(30px)' }}>
          {[
            { icon: ShieldCheck, label: 'Registered NDIS Provider' },
            { icon: ClipboardCheck, label: 'Audit-Approved Support Services' },
            { icon: MapPin, label: 'Mareeba & all Tablelands regions' },
          ].map((row) => (
            <div key={row.label} className="flex items-center gap-3 text-white">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-cyan-400/25 text-cyan-200">
                <row.icon size={16} />
              </span>
              <span className="text-sm font-medium">{row.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-5 border-t border-white/15 flex justify-center" style={{ transform: 'translateZ(30px)' }}>
          <img
            src="/ndis-registered-white.png"
            alt="I love NDIS — Registered NDIS Provider"
            data-testid="hero-ndis-badge"
            className="h-10 w-auto"
          />
        </div>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.16 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.8, ease: easeOut }}
          src="/hero-bg.jpg"
          alt="Support under a bright far north Queensland sky"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-azure/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-navy/30" />
        <div className="grain" />
      </motion.div>

      <motion.div
        className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 pt-32 pb-24 lg:py-36 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center"
      >
        <div className="lg:col-span-7 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8, ease: easeOut }}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/10 backdrop-blur px-4 py-2 text-[11px] lg:text-xs uppercase tracking-[0.24em] font-semibold text-cyan-200"
          >
            <ShieldCheck size={14} />
            Registered &amp; Audit-Approved NDIS Provider
          </motion.div>

          <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.06] text-[clamp(2.6rem,5.6vw,4.7rem)]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: '112%' }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.16, duration: 1, ease: easeOut }}
                  className={`block ${line.accent ? 'italic font-light text-cyan-300' : ''}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: easeOut }}
            className="mt-6 max-w-xl text-base lg:text-lg text-white/85 leading-relaxed"
          >
            Care For All – Support Services walks alongside NDIS participants across Mareeba and
            every Tablelands community — with supports built around your goals, your pace, your life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: easeOut }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-cta-call"
              href="tel:0473619278"
              className="group flex items-center gap-2.5 rounded-full bg-azure px-7 py-4 font-display font-bold text-white shadow-lift transition-all hover:bg-sky-700 hover:-translate-y-0.5"
            >
              <Phone size={18} className="transition-transform group-hover:rotate-12" />
              Call 0473 619 278
            </a>
            <button
              data-testid="hero-cta-supports"
              onClick={() => scrollToId('supports')}
              className="flex items-center gap-2 rounded-full border border-white/35 bg-white/10 backdrop-blur px-7 py-4 font-display font-bold text-white transition-all hover:bg-white/20 hover:-translate-y-0.5"
            >
              Explore our supports
              <ArrowDown size={17} />
            </button>
          </motion.div>

          <div className="mt-10">
            <TiltCard />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-white/70 flex flex-col items-center gap-1 z-10"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="scroll-cue" />
      </motion.div>
    </section>
  );
}
